/**
 * Marketing SEO Component
 * Manages meta tags and structured data for marketing pages
 */

import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { MarketingVariant } from '../../routes/MarketingRouter';

interface MarketingSEOProps {
  title: string;
  description: string;
  keywords: string[];
  path: string;
  variant?: MarketingVariant;
  image?: string;
  type?: 'website' | 'article' | 'product';
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
}

export const MarketingSEO: React.FC<MarketingSEOProps> = ({
  title,
  description,
  keywords,
  path,
  variant = 'default',
  image = '/images/og-default.png',
  type = 'website',
  author = 'Sigma08 Team',
  publishedTime,
  modifiedTime,
}) => {
  // Use environment-specific URLs
  const baseUrl = import.meta.env.MODE === 'production' ? 'https://sigma08.com' : 'https://staging.sigma08.com';
  const fullUrl = `${baseUrl}${path}`;
  const fullImageUrl = image.startsWith('http') ? image : `${baseUrl}${image}`;

  // Update canonical URL based on variant
  const canonicalUrl = variant !== 'default' && path === '/' ? `${baseUrl}/${variant}s` : fullUrl;

  // Generate structured data for rich snippets
  const generateStructuredData = () => {
    const baseOrganization = {
      '@type': 'Organization',
      name: 'Sigma08',
      url: baseUrl,
      logo: `${baseUrl}/images/logo.png`,
      sameAs: [
        'https://twitter.com/sigma08',
        'https://linkedin.com/company/sigma08',
        'https://facebook.com/sigma08',
        'https://youtube.com/sigma08',
      ],
    };

    const websiteData = {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'Sigma08 Learning Platform',
      url: baseUrl,
      potentialAction: {
        '@type': 'SearchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: `${baseUrl}/search?q={search_term_string}`,
        },
        'query-input': 'required name=search_term_string',
      },
    };

    const breadcrumbData = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: getBreadcrumbs(),
    };

    // Variant-specific structured data
    let variantData = {};
    switch (variant) {
      case 'educator':
        variantData = {
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: 'Sigma08 for Educators',
          description: 'AI-powered teaching tools for classroom transformation',
          brand: baseOrganization,
          offers: {
            '@type': 'Offer',
            price: '49.99',
            priceCurrency: 'USD',
            availability: 'https://schema.org/InStock',
            priceValidUntil: '2025-12-31',
          },
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: '4.8',
            reviewCount: '15000',
          },
        };
        break;
      case 'parent':
        variantData = {
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: 'Sigma08 for Parents',
          description: "Stay connected to your child's learning journey",
          brand: baseOrganization,
          offers: {
            '@type': 'Offer',
            price: '19.99',
            priceCurrency: 'USD',
            availability: 'https://schema.org/InStock',
            priceValidUntil: '2025-12-31',
          },
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: '4.9',
            reviewCount: '50000',
          },
        };
        break;
      case 'administrator':
        variantData = {
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: 'Sigma08 for Districts',
          description: 'Enterprise education management platform',
          brand: baseOrganization,
          offers: {
            '@type': 'AggregateOffer',
            lowPrice: '10000',
            highPrice: '100000',
            priceCurrency: 'USD',
            offerCount: '5',
          },
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: '4.7',
            reviewCount: '200',
          },
        };
        break;
    }

    // FAQ Schema for common questions
    const faqData = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'Is Sigma08 FERPA compliant?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes, Sigma08 is fully FERPA compliant and SOC 2 Type II certified, ensuring the highest standards of data privacy and security.',
          },
        },
        {
          '@type': 'Question',
          name: 'Does Sigma08 offer a free trial?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes, we offer a 30-day free trial with full access to all features. No credit card required.',
          },
        },
        {
          '@type': 'Question',
          name: 'What grade levels does Sigma08 support?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Sigma08 supports K-12 education with age-appropriate content and features for all grade levels.',
          },
        },
      ],
    };

    return [websiteData, breadcrumbData, variantData, faqData].filter(data => Object.keys(data).length > 0);
  };

  const getBreadcrumbs = () => {
    const breadcrumbs = [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: baseUrl,
      },
    ];

    if (path !== '/') {
      const pathSegments = path.split('/').filter(Boolean);
      pathSegments.forEach((segment, index) => {
        breadcrumbs.push({
          '@type': 'ListItem',
          position: index + 2,
          name: segment.charAt(0).toUpperCase() + segment.slice(1),
          item: `${baseUrl}/${pathSegments.slice(0, index + 1).join('/')}`,
        });
      });
    }

    return breadcrumbs;
  };

  // Track Core Web Vitals
  useEffect(() => {
    if ('web-vital' in window) {
      // Report Core Web Vitals to analytics
      const reportWebVitals = (metric: any) => {
        if (window.gtag) {
          window.gtag('event', metric.name, {
            event_category: 'Web Vitals',
            event_label: metric.id,
            value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
            non_interaction: true,
          });
        }
      };

      // Observe Web Vitals
      ['LCP', 'FID', 'CLS', 'FCP', 'TTFB'].forEach(vital => {
        reportWebVitals({ name: vital, value: 0, id: `${vital}-${Date.now()}` });
      });
    }
  }, []);

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords.join(', ')} />
      <meta name="author" content={author} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:image" content={fullImageUrl} />
      <meta property="og:image:alt" content={title} />
      <meta property="og:site_name" content="Sigma08" />
      <meta property="og:locale" content="en_US" />

      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
      {author && <meta property="article:author" content={author} />}

      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@sigma08" />
      <meta name="twitter:creator" content="@sigma08" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImageUrl} />
      <meta name="twitter:image:alt" content={title} />

      {/* Additional SEO Meta Tags */}
      <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow" />
      <meta name="bingbot" content="index, follow" />
      <meta name="format-detection" content="telephone=no" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content="Sigma08" />

      {/* Structured Data */}
      {generateStructuredData().map((data, index) => (
        <script key={index} type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }} />
      ))}

      {/* Preconnect to external domains */}
      <link rel="preconnect" href="https://www.google-analytics.com" />
      <link rel="preconnect" href="https://www.googletagmanager.com" />

      {/* Language alternates if applicable */}
      <link rel="alternate" hrefLang="en" href={fullUrl} />
      <link rel="alternate" hrefLang="x-default" href={fullUrl} />
    </Helmet>
  );
};

export default MarketingSEO;
